/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable prefer-rest-params */
/* eslint-disable @typescript-eslint/ban-types */
import { Subject, Unsubscribable } from 'rxjs';

/**
 * https://induro.io/blog/2018/08/angular-2-observable_life-cycle_events/
 *
 * Creates an observable property on an object that will
 * emit when the corresponding life-cycle event occurs.
 * The main rules are:
 * 1. Don't name the property the same as the angular interface method.
 * 2. If a class inherits from another component where the parent uses this decorator
 *    and the child implements the corresponding interface then it needs to call the parent method.
 *
 * @example Usage examples
 * ```ts
 * @OnChangesObservable
 * onChanges: Observable<SimpleChanges>;
 *
 * @OnInitObservable
 * onInit: Observable<never>;
 *
 * @DoCheckObservable
 * doCheck: Observable<never>;
 *
 * @AfterContentInitObservable
 * afterContentInit: Observable<never>;
 *
 * @AfterContentCheckedObservable
 * afterContentChecked: Observable<never>;
 *
 * @AfterViewInitObservable
 * afterViewInit: Observable<never>;
 *
 * @AfterViewCheckedObservable
 * afterViewChecked: Observable<never>;
 *
 * @OnDestroyObservable
 * onDestroy: Observable<never>;
 *
 * this.myService.takeUntil(this.onDestroy).subscribe(() => {});
 * this.onChanges.map(changes => changes.input).takeUntil(this.onDestroy).subscribe(() => {});
 * ```
 *
 * @param {string} lifeCycleMethodName name of the function that angular calls for the life-cycle event
 * @param {object} target class that contains the decorated property
 * @param {string} propertyKey name of the decorated property
 *
 */
function applyLifeCycleObservable(lifeCycleMethodName: string, target: object, propertyKey: string): void {
	// Save a reference to the original life-cycle callback so that we can call it if it exists.
	const originalLifeCycleMethod = target.constructor.prototype[lifeCycleMethodName];

	// Use a symbol to make the observable for the instance unobtrusive.
	const instanceSubjectKey = Symbol(propertyKey);
	Object.defineProperty(target, propertyKey, {
		get: function() {
			// Get the observable for this instance or create it.
			return (this[instanceSubjectKey] || (this[instanceSubjectKey] = new Subject())).asObservable();
		}
	});

	// Add or override the life-cycle callback.
	target.constructor.prototype[lifeCycleMethodName] = function() {
		// If it hasn't been created then there no subscribers so there is no need to emit
		if (this[instanceSubjectKey]) {
			// Emit the life-cycle event.
			// We pass the first parameter because onChanges has a SimpleChanges parameter.
			this[instanceSubjectKey].next.call(this[instanceSubjectKey], arguments[0]);
		}

		// If the object already had a life-cycle callback then invoke it.
		if (originalLifeCycleMethod && typeof originalLifeCycleMethod === 'function') {
			originalLifeCycleMethod.apply(this, arguments);
		}
	};
}

// Property Decorators
export function OnChangesObservable(target: any, propertyKey: string): void {
	applyLifeCycleObservable('ngOnChanges', target, propertyKey);
}
export function OnInitObservable(target: any, propertyKey: string): void {
	applyLifeCycleObservable('ngOnInit', target, propertyKey);
}
export function DoCheckObservable(target: any, propertyKey: string): void {
	applyLifeCycleObservable('ngDoCheck', target, propertyKey);
}
export function AfterContentInitObservable(target: any, propertyKey: string): void {
	applyLifeCycleObservable('ngAfterContentInit', target, propertyKey);
}
export function AfterContentCheckedObservable(target: any, propertyKey: string): void {
	applyLifeCycleObservable('ngAfterContentChecked', target, propertyKey);
}
export function AfterViewInitObservable(target: any, propertyKey: string): void {
	applyLifeCycleObservable('ngAfterViewInit', target, propertyKey);
}
export function AfterViewCheckedObservable(target: any, propertyKey: string): void {
	applyLifeCycleObservable('ngAfterViewChecked', target, propertyKey);
}
export function OnDestroyObservable(target: any, propertyKey: string): void {
	applyLifeCycleObservable('ngOnDestroy', target, propertyKey);
}

export function UnsubscribeOnDestroy<Key extends PropertyKey>(target: object, propertyKey: Key): void {
	const oldOnDestroy: (() => void) | null = target['ngOnDestroy'];
	
	target['ngOnDestroy'] = function (this: {[key in Key]: Unsubscribable}) {
		this[propertyKey].unsubscribe();
		oldOnDestroy?.call(this);
	};
}